'use client';

import { HStack, token } from '@terra/ui-styled-system';
import {
  Alert01Icon,
  CheckmarkCircle01Icon,
  Loading03Icon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { Tooltip } from '../../components/tooltip';
import { BodyDefault } from '../../components/typography';
import type { WafStatusForDomain } from '../domain-manager/waf-status';
import {
  resolveApplicationUrlWafStatusDisplay,
  type ApplicationUrlWafStatusIcon,
  type ApplicationUrlWafStatusTone,
} from './application-url-waf-status.model';

export type ApplicationUrlWafStatusProps = {
  wafStatus: WafStatusForDomain;
  url?: string;
};

const TEXT_COLOR_BY_TONE: Record<ApplicationUrlWafStatusTone, string> = {
  muted: 'fg.neutral.muted',
  success: 'fg.neutral.success',
  error: 'fg.neutral.error',
  warning: 'fg.neutral.warning',
};

const ICON_COLOR_BY_TONE: Record<
  ApplicationUrlWafStatusTone,
  string | undefined
> = {
  muted: undefined,
  success: token('colors.fg.neutral.success'),
  error: token('colors.fg.neutral.error'),
  warning: token('colors.fg.neutral.warning'),
};

const ICON_BY_KIND = {
  loading: Loading03Icon,
  check: CheckmarkCircle01Icon,
  alert: Alert01Icon,
} as const satisfies Record<ApplicationUrlWafStatusIcon, unknown>;

const SPIN_STYLE = { animation: 'spin 1s linear infinite' };

export function ApplicationUrlWafStatus({
  wafStatus,
  url,
}: ApplicationUrlWafStatusProps) {
  const t = useTranslations('designSystem.applicationGeneral.wafStatus');
  const display = resolveApplicationUrlWafStatusDisplay({ wafStatus, url });

  if (!display) {
    return null;
  }

  const StatusIcon = ICON_BY_KIND[display.icon];

  return (
    <Tooltip
      title={t(display.tooltipKey)}
      maxW="320px"
      positioning={{ strategy: 'fixed' }}
    >
      <HStack gap={2} alignItems="center" display="inline-flex">
        <StatusIcon
          size={14}
          aria-hidden
          color={ICON_COLOR_BY_TONE[display.tone]}
          style={display.icon === 'loading' ? SPIN_STYLE : undefined}
        />
        <BodyDefault color={TEXT_COLOR_BY_TONE[display.tone]}>
          {t(display.messageKey)}
        </BodyDefault>
      </HStack>
    </Tooltip>
  );
}
